import { useState, useEffect, useCallback, useRef } from "react";
import debounce from "lodash.debounce";

export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- generic constraint requires any for structural typing compatibility
export function useDebouncedCallback<T extends (...args: any[]) => any>(
  callback: T,
  delay: number = 300
): (...args: Parameters<T>) => void {
  const debouncedFnRef = useRef<ReturnType<typeof debounce>>(null);

  useEffect(() => {
    debouncedFnRef.current = debounce(callback, delay);

    return () => {
      debouncedFnRef.current?.cancel();
    };
  }, [callback, delay]);

  return useCallback((...args: Parameters<T>) => {
    debouncedFnRef.current?.(...args);
  }, []);
}
